import { type ApiError, useApi } from '@omnipresentgroup/api';
import type {
  CreateCompensationVariantPaymentRequest,
  CreateCompensationVariantPaymentResponse,
  CreateCompensationVariantPaymentResponseAPI,
} from '@omnipresentgroup/types';
import {
  type UseMutationOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';

import { API_BASE, VARIANT_PAYMENTS_API_PATH } from '../constants';
import {
  transformCreateCompensationVariantPaymentRequestToAPI,
  transformCreateCompensationVariantPaymentResponseFromAPI,
} from '../mappers';

export const useCreateCompensationVariantPaymentMutation = ({
  employeeId,
  onSuccess,
  ...options
}: Omit<
  UseMutationOptions<
    CreateCompensationVariantPaymentResponse,
    ApiError,
    CreateCompensationVariantPaymentRequest
  >,
  'mutationFn'
> & {
  employeeId: string;
}) => {
  const { POST } = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload) => {
      return new Promise<CreateCompensationVariantPaymentResponse>(
        (resolve, reject) => {
          POST<CreateCompensationVariantPaymentResponseAPI>({
            path: `${API_BASE}/${VARIANT_PAYMENTS_API_PATH}`,
            body: transformCreateCompensationVariantPaymentRequestToAPI(
              payload,
            ),
          })
            .then((response) =>
              resolve(
                transformCreateCompensationVariantPaymentResponseFromAPI(
                  response,
                ),
              ),
            )
            .catch((error) => reject(error));
        },
      );
    },
    onSuccess: (data, variables, context) => {
      !variables?.dryRun &&
        queryClient.invalidateQueries({
          queryKey: [VARIANT_PAYMENTS_API_PATH, employeeId],
          refetchType: 'all',
        });
      onSuccess?.(data, variables, context);
    },
    ...options,
  });
};
